import { OrthogonalPathPoint, stripRedundantPoints } from './orthogonalPath';

export const ORTHOGONAL_CROSSING_GAP_RADIUS = 4;

export interface CrossingGap extends OrthogonalPathPoint {
  /** Outline of the other edge's rounded bend, used to mask this rail only. */
  curve?: string;
}

/**
 * Read points from getSmoothStepPath with borderRadius: 0. Its absolute M/L/Q
 * commands contain coordinate pairs; each zero-radius Q repeats its corner.
 * This is not a parser for arbitrary SVG paths or nonzero-radius curves.
 */
export function getUnroundedSmoothStepPoints(
  path: string
): OrthogonalPathPoint[] {
  const coordinates = (
    path.match(/[-+]?(?:\d*\.?\d+)(?:e[-+]?\d+)?/gi) ?? []
  ).map(Number);
  const points: OrthogonalPathPoint[] = [];
  for (let index = 0; index + 1 < coordinates.length; index += 2) {
    const point = { x: coordinates[index]!, y: coordinates[index + 1]! };
    const previous = points.at(-1);
    if (!previous || previous.x !== point.x || previous.y !== point.y)
      points.push(point);
  }
  return points;
}

interface Segment {
  start: OrthogonalPathPoint;
  end: OrthogonalPathPoint;
}

function strictlyBetween(
  value: number,
  first: number,
  second: number
): boolean {
  return value > Math.min(first, second) && value < Math.max(first, second);
}

function segments(points: OrthogonalPathPoint[]): Segment[] {
  return points.slice(1).map((end, index) => ({
    start: points[index]!,
    end,
  }));
}

function samePoint(
  a: OrthogonalPathPoint | undefined,
  b: OrthogonalPathPoint | undefined
): boolean {
  return !!a && !!b && a.x === b.x && a.y === b.y;
}

/**
 * Horizontal rails yield to vertical crossings and converging rounded bends.
 * Convergence requires a shared target handle and travel into the rail in the
 * same direction. Fan-out junctions stay continuous. A bend's outline masks
 * only the through-rail. Measured radii enable curved clearance. Without
 * radii, return only straight crossings until the live renderer registers.
 */
export function findOrthogonalCrossings(
  paths: Map<string, OrthogonalPathPoint[]>,
  borderRadii?: Map<string, number>
): Map<string, CrossingGap[]> {
  const crossings = new Map<string, CrossingGap[]>();
  const entries = Array.from(
    paths,
    ([id, points]) => [id, stripRedundantPoints(points)] as const
  );

  for (let firstIndex = 0; firstIndex < entries.length; firstIndex++) {
    const [firstId, firstPoints] = entries[firstIndex]!;
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < entries.length;
      secondIndex++
    ) {
      const [secondId, secondPoints] = entries[secondIndex]!;
      for (const first of segments(firstPoints)) {
        for (const second of segments(secondPoints)) {
          const firstIsHorizontal = first.start.y === first.end.y;
          const secondIsHorizontal = second.start.y === second.end.y;
          const firstIsVertical = first.start.x === first.end.x;
          const secondIsVertical = second.start.x === second.end.x;

          let horizontal: Segment;
          let vertical: Segment;
          let horizontalId: string;
          let verticalId: string;
          let verticalPoints: OrthogonalPathPoint[];
          if (firstIsHorizontal && secondIsVertical) {
            horizontal = first;
            vertical = second;
            horizontalId = firstId;
            verticalId = secondId;
            verticalPoints = secondPoints;
          } else if (secondIsHorizontal && firstIsVertical) {
            horizontal = second;
            vertical = first;
            horizontalId = secondId;
            verticalId = firstId;
            verticalPoints = firstPoints;
          } else {
            continue;
          }

          const point: CrossingGap = {
            x: vertical.start.x,
            y: horizontal.start.y,
          };
          if (!strictlyBetween(point.x, horizontal.start.x, horizontal.end.x)) {
            continue;
          }

          if (!strictlyBetween(point.y, vertical.start.y, vertical.end.y)) {
            const horizontalPoints =
              horizontalId === firstId ? firstPoints : secondPoints;
            if (
              !samePoint(point, vertical.end) ||
              !samePoint(horizontalPoints.at(-1), verticalPoints.at(-1))
            )
              continue;
            // Use the segment's vertex, not a coordinate search: a route can
            // revisit the same coordinate later without sharing that corner.
            const index = verticalPoints.indexOf(vertical.end);
            if (index <= 0 || index >= verticalPoints.length - 1) continue;
            const previous = verticalPoints[index - 1]!;
            const next = verticalPoints[index + 1]!;
            if (
              Math.sign(next.x - point.x) !==
              Math.sign(horizontal.end.x - horizontal.start.x)
            )
              continue;
            const radius = Math.min(
              borderRadii?.get(verticalId) ?? 0,
              (Math.abs(point.x - previous.x) +
                Math.abs(point.y - previous.y)) /
                2,
              (Math.abs(next.x - point.x) + Math.abs(next.y - point.y)) / 2
            );
            if (radius <= 0) continue;
            const entry = {
              x: point.x + Math.sign(previous.x - point.x) * radius,
              y: point.y + Math.sign(previous.y - point.y) * radius,
            };
            const exit = {
              x: point.x + Math.sign(next.x - point.x) * radius,
              y: point.y + Math.sign(next.y - point.y) * radius,
            };
            point.curve = `M ${entry.x} ${entry.y} Q ${point.x} ${point.y} ${exit.x} ${exit.y}`;
          }

          const edgeCrossings = crossings.get(horizontalId) ?? [];
          if (
            !edgeCrossings.some(
              ({ x, y, curve }) =>
                x === point.x && y === point.y && curve === point.curve
            )
          ) {
            edgeCrossings.push(point);
            crossings.set(horizontalId, edgeCrossings);
          }
        }
      }
    }
  }

  return crossings;
}

/** Keep clearance masks on through-rails, never on their own rounded bends. */
export function filterCrossingsForRoundedPath(
  crossings: CrossingGap[],
  points: OrthogonalPathPoint[],
  borderRadius: number
): CrossingGap[] {
  const aligned = stripRedundantPoints(points);
  return crossings.flatMap((crossing) => {
    const fitsStraight = segments(aligned).some((segment, index) => {
      if (segment.start.y !== segment.end.y || crossing.y !== segment.start.y) {
        return false;
      }

      const previous = aligned[index - 1];
      const next = aligned[index + 2];
      const startCornerRadius = previous
        ? Math.min(
            borderRadius,
            Math.abs(segment.start.y - previous.y) / 2,
            Math.abs(segment.end.x - segment.start.x) / 2
          )
        : 0;
      const endCornerRadius = next
        ? Math.min(
            borderRadius,
            Math.abs(next.y - segment.end.y) / 2,
            Math.abs(segment.end.x - segment.start.x) / 2
          )
        : 0;
      return (
        strictlyBetween(crossing.x, segment.start.x, segment.end.x) &&
        Math.abs(crossing.x - segment.start.x) >=
          startCornerRadius + ORTHOGONAL_CROSSING_GAP_RADIUS &&
        Math.abs(segment.end.x - crossing.x) >=
          endCornerRadius + ORTHOGONAL_CROSSING_GAP_RADIUS
      );
    });
    return fitsStraight ? [crossing] : [];
  });
}
